import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import {
  useFirebase,
  useFirestoreConnect,
  isLoaded,
} from 'react-redux-firebase';
import moment from 'moment';
import { useCallbackRef } from 'use-callback-ref';

import {
  annotationPhotoDocumentDataSourceChangeInit,
  annotationPhotoDocumentDataSourceChange,
} from '../../actions/Annotation/AnnotationActions';

import LoadingData from '../LoadingData';
import NoResults from '../NoResults';
import Select from '../Select';
import DataSourceManage from '../DataSourceAdd';
import AnnotationCanvas from '../AnnotationCanvas';
import AnnotationSequence from '../AnnotationSequence';

import { useTheme } from '../../ThemeContext';

import {
  Wrapper,
  Holder,
  Panel,
  ImageHolder,
  Video,
  Image,
  Placeholder,
  ImageMenu,
  ContentHolder,
  Header,
  Title,
  Content,
  Details,
  Detail,
  DetailLabel,
  Button,
  ButtonIcon,
  ActionButton,
  ActionButtonIcon,
  Category,
  CategoryTitle,
  CategoryValues,
  CategoryValue,
  Switcher,
  SwitcherButton,
  Selectbox,
  Arrow,
  ArrowIcon,
} from './SingleAnnotation.style';

import roles from '../../roles';

const SingleAnnotation = ({
  data = {},
  dataDetails = [],
  handleDeletePhotoDocument = () => {},
  handleSaveAnnotation = () => {},
  handleFinishAnnotation = () => {},
  handleFilterAnnotation = () => {},
  handleChangeAnnotation = () => {},
  photoTagsAnnotation,
  changedPhotoTagsAnnotation,
  objectsAnnotation,
  changedObjectsAnnotation,
  handleSelectPhotoDocuments = () => {},
  selectedObject,
  handleSelectObject = () => {},
  isLoading = false,
  availableRejectAnnotation = false,
  availablePreviousAnnotation = false,
  previousAnnotation,
  handleChangePhotoDocument = () => {},
  availableDeleteAnnotation = false,
  finishButtonMessage = '',
  isSequence = false,
  sequence = [],
}) => {
  const [update, forceUpdate] = useState(0);
  const mediaRef = useCallbackRef(null, () => forceUpdate(update + 1));
  const intl = useIntl();
  const theme = useTheme();
  const firebase = useFirebase();
  const dispatch = useDispatch();
  const { auth, profile } = useSelector((state) => state.firebase);
  const isProfileCompany = profile.company && profile.company.id ? true : false;
  const profileCompany = useSelector(
    (state) => state.firestore.data['profileCompany']
  );
  const profileCompanyRole =
    profileCompany &&
    profileCompany.admins &&
    profileCompany.admins.length > 0 &&
    profileCompany.admins.find((admin) => admin.id === auth.uid)
      ? 'isAdmin'
      : profileCompany &&
        profileCompany.users &&
        profileCompany.users.length > 0 &&
        profileCompany.users.find((user) => user.id === auth.uid)
      ? 'isUser'
      : undefined;
  const authRole = roles.find(
    (role) => profileCompanyRole && role.id === profileCompanyRole
  );
  const [storageVideo, setStorageVideo] = useState(undefined);
  const [storageImage, setStorageImage] = useState(undefined);
  const [storageImageError, setStorageImageError] = useState(false);
  const [annotationType, setAnnotationType] = useState(undefined);
  const [visibleDataSourcesAddModal, setVisibleDataSourcesAddModal] = useState(
    false
  );
  const [selectedDataSource, setSelectedDataSource] = useState(undefined);
  const [visibleImageMenu, setVisibleImageMenu] = useState(true);

  const dataSources = useSelector(
    (state) => state.firestore.ordered[`data_sources`]
  );
  const dataSource = useSelector(
    (state) =>
      data.data_source_id &&
      state.firestore.data[`data_sources/${data.data_source_id}`]
  );
  const dataSourcePhotoCategories = useSelector(
    (state) =>
      data.data_source_id &&
      state.firestore.ordered[
        `data_sources/${data.data_source_id}/photo_categories`
      ]
  );
  const dataSourceObjectsCategories = useSelector(
    (state) =>
      data.data_source_id &&
      state.firestore.ordered[
        `data_sources/${data.data_source_id}/objects_categories`
      ]
  );

  const handleChangeDataSource = async (dataSource) => {
    if (isProfileCompany) {
      const result = await dispatch(
        annotationPhotoDocumentDataSourceChange({
          id: data.id,
          dataSourceId: dataSource,
        })
      );
      if (result) dispatch(annotationPhotoDocumentDataSourceChangeInit());
    }
  };

  const handleAction = (action = {}) => {
    if (action.type === 'EDIT') {
      setVisibleDataSourcesAddModal(true);
      setSelectedDataSource(action.payload);
    } else {
      setVisibleDataSourcesAddModal(false);
      setSelectedDataSource(undefined);
    }
  };

  const handleSetVisibleImageMenu = (value) => setVisibleImageMenu(value);

  const firestoreConnect = [];
  if (isProfileCompany) {
    firestoreConnect.push({
      collection: 'companies',
      doc: profile.company.id,
      storeAs: 'profileCompany',
    });
    if (!data.data_source_id && isLoaded(dataSource) && dataSource) {
      firestoreConnect.push({
        collection: 'companies',
        doc: profile.company.id,
        subcollections: [
          {
            collection: 'data_sources',
          },
        ],
        storeAs: `data_sources`,
      });
    } else if (data.data_source_id) {
      firestoreConnect.push({
        collection: 'companies',
        doc: profile.company.id,
        subcollections: [
          {
            collection: 'data_sources',
            doc: data.data_source_id,
          },
        ],
        storeAs: `data_sources/${data.data_source_id}`,
      });
      firestoreConnect.push({
        collection: 'companies',
        doc: profile.company.id,
        subcollections: [
          {
            collection: 'data_sources',
            doc: data.data_source_id,
            subcollections: [{ collection: 'photo_categories' }],
          },
        ],
        storeAs: `data_sources/${data.data_source_id}/photo_categories`,
      });
      firestoreConnect.push({
        collection: 'companies',
        doc: profile.company.id,
        subcollections: [
          {
            collection: 'data_sources',
            doc: data.data_source_id,
            subcollections: [{ collection: 'objects_categories' }],
          },
        ],
        storeAs: `data_sources/${data.data_source_id}/objects_categories`,
      });
    }
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    if (data) handleSelectPhotoDocuments([data]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    let didCancel = false;
    if (data && (data.video_id || data.photo_id))
      (async () => {
        try {
          if (data.video_id) {
            const storageVideo = await firebase
              .storage()
              .ref()
              .child(`waste_photos/${data.video_id}.mp4`)
              .getDownloadURL();
            if (!didCancel) setStorageVideo(storageVideo);
          } else if (data.photo_id) {
            const storageImage = await firebase
              .storage()
              .ref()
              .child(`waste_photos/${data.photo_id}.jpg`)
              .getDownloadURL();
            if (!didCancel) setStorageImage(storageImage);
          }
        } catch {
          if (!didCancel) setStorageImageError(true);
        }
      })();

    return () => {
      didCancel = true;
      setStorageVideo(undefined);
      setStorageImage(undefined);
      setStorageImageError(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && (data.video_id || data.photo_id)]);

  useEffect(() => {
    if (
      !isLoading &&
      photoTagsAnnotation &&
      !changedPhotoTagsAnnotation &&
      dataSourcePhotoCategories &&
      isLoaded(dataSourcePhotoCategories)
    ) {
      handleFilterAnnotation(
        'PHOTO',
        Object.fromEntries(
          Object.entries(photoTagsAnnotation).filter(([category, tag]) => {
            const dataSourceCategory = dataSourcePhotoCategories.find(
              (dataSourceCategory) =>
                String(dataSourceCategory.id) === String(category) &&
                dataSourceCategory.tags &&
                Object.keys(dataSourceCategory.tags).includes(tag)
            );
            return dataSourceCategory ? true : false;
          })
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoTagsAnnotation, dataSourcePhotoCategories]);

  useEffect(() => {
    if (
      !isLoading &&
      objectsAnnotation &&
      !changedObjectsAnnotation &&
      dataSourceObjectsCategories &&
      isLoaded(dataSourceObjectsCategories)
    ) {
      handleFilterAnnotation(
        'OBJECT',
        objectsAnnotation.map((object) => ({
          ...object,
          tags:
            object.tags &&
            Object.fromEntries(
              Object.entries(object.tags).filter(([category, tag]) => {
                const dataSourceCategory = dataSourceObjectsCategories.find(
                  (dataSourceCategory) =>
                    String(dataSourceCategory.id) === String(category) &&
                    dataSourceCategory.tags &&
                    Object.keys(dataSourceCategory.tags).includes(tag)
                );
                return dataSourceCategory ? true : false;
              })
            ),
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectsAnnotation, dataSourceObjectsCategories]);

  useEffect(() => {
    if (
      dataSourcePhotoCategories &&
      isLoaded(dataSourcePhotoCategories) &&
      dataSourceObjectsCategories &&
      isLoaded(dataSourceObjectsCategories)
    ) {
      if (
        (dataSourcePhotoCategories.length > 0 &&
          dataSourceObjectsCategories.length > 0) ||
        (dataSourcePhotoCategories.length > 0 &&
          dataSourceObjectsCategories.length === 0)
      ) {
        setAnnotationType('PHOTO');
      } else if (
        dataSourcePhotoCategories.length === 0 &&
        dataSourceObjectsCategories.length > 0
      ) {
        setAnnotationType('OBJECT');
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourcePhotoCategories, dataSourceObjectsCategories]);

  const renderDataDetail = (detail) => {
    return (
      <Detail theme={theme.annotation.detail}>
        <DetailLabel theme={theme.annotation.detailLabel}>
          {detail.name}:
        </DetailLabel>{' '}
        {detail.value}
      </Detail>
    );
  };

  const renderCategoryBox = (type, category) => {
    return (
      <Category>
        <CategoryTitle theme={theme.annotation.categoryTitle}>
          {category.name || category.id}
        </CategoryTitle>
        <CategoryValues>
          {category.tags &&
            Object.entries(category.tags).map(([id, tag]) => {
              const changedAnnotationTag =
                type === 'PHOTO'
                  ? changedPhotoTagsAnnotation &&
                    changedPhotoTagsAnnotation[category.id]
                  : type === 'OBJECT'
                  ? (() => {
                      const changedObjectAnnotation =
                        changedObjectsAnnotation &&
                        changedObjectsAnnotation.find(
                          (object) => object.id === selectedObject
                        );
                      return (
                        changedObjectAnnotation &&
                        changedObjectAnnotation.tags &&
                        changedObjectAnnotation.tags[category.id]
                      );
                    })()
                  : undefined;

              return (
                <CategoryValue
                  theme={theme.annotation.categoryValue}
                  key={id}
                  selected={
                    changedAnnotationTag
                      ? changedAnnotationTag === id
                        ? true
                        : undefined
                      : undefined
                  }
                  onClick={() => {
                    handleChangeAnnotation(type, {
                      categoryId: category.id,
                      tag: id,
                    });
                  }}
                >
                  {tag || id}
                </CategoryValue>
              );
            })}
        </CategoryValues>
      </Category>
    );
  };

  const renderDataSourceAssign = () => {
    return (
      <Selectbox>
        <Select
          options={
            dataSources &&
            dataSources.length > 0 &&
            dataSources.map((dataSource) => {
              return {
                ...dataSource,
                value: dataSource.id,
                label: dataSource.name,
              };
            })
          }
          placeholderMessage={'ANNOTATION.ASSIGN_DATA_SOURCE'}
          onChange={(value) => {
            handleChangeDataSource(value);
          }}
          small
        />
      </Selectbox>
    );
  };

  const renderPreviewPanel = () => {
    return (
      <Panel theme={theme.annotation.panel}>
        <Details>
          {dataDetails.map((detail, key) => (
            <Fragment key={key}>{renderDataDetail(detail)}</Fragment>
          ))}
        </Details>
        <ImageHolder theme={theme.annotation.imageHolder}>
          {renderSequenceLeftArrow()}
          {data.video_id || data.photo_id ? (
            storageVideo ? (
              <>
                <Video
                  ref={mediaRef}
                  src={storageVideo}
                  alt={data.video_id}
                  loading='lazy'
                  playsInline
                  autoPlay
                  muted
                  loop
                  onLoadedData={() => forceUpdate(update + 1)}
                >
                  <source src={storageVideo} type='video/mp4'></source>
                </Video>
                {annotationType === 'OBJECT' &&
                mediaRef.current &&
                mediaRef.current.videoWidth &&
                mediaRef.current.videoHeight ? (
                  <AnnotationCanvas
                    mediaWidth={mediaRef.current.videoWidth}
                    mediaHeight={mediaRef.current.videoHeight}
                    objects={changedObjectsAnnotation}
                    selectedObject={selectedObject}
                    handleSelectObject={handleSelectObject}
                    handleChangeAnnotation={handleChangeAnnotation}
                    handleSetVisibleImageMenu={handleSetVisibleImageMenu}
                  />
                ) : (
                  ''
                )}
              </>
            ) : storageImage ? (
              <>
                <Image
                  ref={mediaRef}
                  src={storageImage}
                  alt={data.photo_id}
                  loading='lazy'
                  onLoad={() => forceUpdate(update + 1)}
                />
                {annotationType === 'OBJECT' &&
                mediaRef.current &&
                mediaRef.current.naturalWidth &&
                mediaRef.current.naturalHeight ? (
                  <AnnotationCanvas
                    mediaWidth={mediaRef.current.naturalWidth}
                    mediaHeight={mediaRef.current.naturalHeight}
                    objects={changedObjectsAnnotation}
                    selectedObject={selectedObject}
                    handleSelectObject={handleSelectObject}
                    handleChangeAnnotation={handleChangeAnnotation}
                    handleSetVisibleImageMenu={handleSetVisibleImageMenu}
                  />
                ) : (
                  ''
                )}
              </>
            ) : storageImageError ? (
              <Placeholder className='material-icons'>
                image_not_supported
              </Placeholder>
            ) : (
              ''
            )
          ) : (
            <Placeholder className='material-icons'>
              image_not_supported
            </Placeholder>
          )}

          {visibleImageMenu ? (
            <>
              {availableRejectAnnotation ? (
                <ImageMenu top right>
                  <Button
                    theme={theme.button.normal}
                    widthAuto
                    noMask
                    small
                    disabled={isLoading}
                    onClick={() => {
                      if (!isLoading)
                        handleSaveAnnotation({ isRejected: true });
                    }}
                  >
                    {isLoading
                      ? intl.formatMessage({
                          id: 'ANNOTATION.PLEASE_WAIT',
                        })
                      : intl.formatMessage({ id: 'ANNOTATION.REJECT' })}
                  </Button>
                </ImageMenu>
              ) : (
                ''
              )}
              {availablePreviousAnnotation ? (
                <ImageMenu top left>
                  <Button
                    theme={theme.button.normal}
                    widthAuto
                    noMask
                    small
                    disabled={
                      isLoading ||
                      !(previousAnnotation && previousAnnotation.length > 0)
                        ? true
                        : undefined
                    }
                    onClick={() => {
                      if (
                        !(
                          isLoading ||
                          !(previousAnnotation && previousAnnotation.length > 0)
                        )
                      )
                        handleChangePhotoDocument();
                    }}
                  >
                    <ButtonIcon className='material-icons'>
                      keyboard_backspace
                    </ButtonIcon>
                    {intl.formatMessage({ id: 'ANNOTATION.PREVIOUS' })}
                  </Button>
                </ImageMenu>
              ) : (
                ''
              )}
              {availableDeleteAnnotation &&
              isProfileCompany &&
              isLoaded(profileCompany) &&
              profileCompanyRole &&
              authRole &&
              authRole.privileges &&
              authRole.privileges['DELETE_PHOTO'] ? (
                <ImageMenu bottom right>
                  <Button
                    theme={theme.button.danger}
                    widthAuto
                    noMask
                    small
                    disabled={isLoading}
                    onClick={() => {
                      if (!isLoading) handleDeletePhotoDocument();
                    }}
                  >
                    <ButtonIcon className='material-icons'>
                      delete_outline
                    </ButtonIcon>
                    {isLoading
                      ? intl.formatMessage({
                          id: 'ANNOTATION.PLEASE_WAIT',
                        })
                      : intl.formatMessage({ id: 'ANNOTATION.DELETE' })}
                  </Button>
                </ImageMenu>
              ) : (
                ''
              )}

              {dataSourcePhotoCategories &&
              dataSourcePhotoCategories.length > 0 &&
              dataSourceObjectsCategories &&
              dataSourceObjectsCategories.length > 0 ? (
                <ImageMenu bottom left>
                  <Switcher>
                    <SwitcherButton
                      theme={
                        annotationType === 'PHOTO'
                          ? theme.button.primary
                          : theme.button.normal
                      }
                      widthAuto
                      noMask
                      small
                      onClick={() => setAnnotationType('PHOTO')}
                    >
                      {intl.formatMessage({
                        id: 'ANNOTATION.LABEL_PHOTO',
                      })}
                    </SwitcherButton>
                    <SwitcherButton
                      theme={
                        annotationType === 'OBJECT'
                          ? theme.button.primary
                          : theme.button.normal
                      }
                      widthAuto
                      noMask
                      small
                      onClick={() => setAnnotationType('OBJECT')}
                    >
                      {intl.formatMessage({
                        id: 'ANNOTATION.LABEL_OBJECTS',
                      })}
                    </SwitcherButton>
                  </Switcher>
                </ImageMenu>
              ) : (
                ''
              )}
            </>
          ) : (
            ''
          )}
          {renderSequenceRightArrow()}
        </ImageHolder>
      </Panel>
    );
  };

  const renderSequencePanel = () => {
    return (
      <>
        {isSequence ? (
          <Panel theme={theme.annotation.panel} height={'auto'}>
            <AnnotationSequence
              data={sequence}
              selectedPhotoDocument={data}
              isLoading={isLoading}
              handleSelectPhotoDocument={(data) =>
                handleSelectPhotoDocuments([data])
              }
              handleSaveAnnotations={() =>
                handleSaveAnnotation({ sequenceSave: true })
              }
            />
          </Panel>
        ) : (
          ''
        )}
      </>
    );
  };

  const renderSequenceLeftArrow = () => {
    const photoDocumentIndex = sequence.findIndex(
      (photo) => String(photo.id) === String(data.id)
    );
    const availableChangeLeftPhotoDocument =
      photoDocumentIndex !== -1 && photoDocumentIndex > 0 ? true : false;
    return (
      <>
        {isSequence && availableChangeLeftPhotoDocument ? (
          <Arrow
            theme={theme.annotation.arrow}
            left
            onClick={() =>
              handleSelectPhotoDocuments([sequence[photoDocumentIndex - 1]])
            }
          >
            <ArrowIcon className='material-icons'>
              keyboard_arrow_left
            </ArrowIcon>
          </Arrow>
        ) : (
          ''
        )}
      </>
    );
  };

  const renderSequenceRightArrow = () => {
    const photoDocumentIndex = sequence.findIndex(
      (photo) => String(photo.id) === String(data.id)
    );
    const availableChangeRightPhotoDocument =
      photoDocumentIndex !== -1 && photoDocumentIndex < sequence.length - 1
        ? true
        : false;
    return (
      <>
        {isSequence && availableChangeRightPhotoDocument ? (
          <Arrow
            theme={theme.annotation.arrow}
            right
            onClick={() =>
              handleSelectPhotoDocuments([sequence[photoDocumentIndex + 1]])
            }
          >
            <ArrowIcon className='material-icons'>
              keyboard_arrow_right
            </ArrowIcon>
          </Arrow>
        ) : (
          ''
        )}
      </>
    );
  };

  return (
    <>
      <Wrapper>
        <Holder>
          {renderPreviewPanel()}
          {renderSequencePanel()}
        </Holder>
        <Holder>
          <Panel theme={theme.annotation.panel}>
            <ContentHolder theme={theme.photoDocumentDetails.contentHolder}>
              <Header theme={theme.photoDocumentDetails.header}>
                {data.data_source_id ? (
                  isLoaded(dataSource) ? (
                    dataSource ? (
                      <Title theme={theme.photoDocumentDetails.title}>
                        {dataSource.name}
                        {isLoaded(dataSourcePhotoCategories) &&
                        isLoaded(dataSourceObjectsCategories) ? (
                          <ActionButton
                            small
                            square
                            noMask
                            rounded
                            theme={theme.button.normal}
                            onClick={() =>
                              handleAction({
                                type: 'EDIT',
                                payload: {
                                  ...dataSource,
                                  id: data.data_source_id,
                                  photoCategories: dataSourcePhotoCategories,
                                  objectsCategories: dataSourceObjectsCategories,
                                },
                              })
                            }
                          >
                            <ActionButtonIcon className='material-icons-outlined'>
                              edit
                            </ActionButtonIcon>
                          </ActionButton>
                        ) : (
                          ''
                        )}
                      </Title>
                    ) : (
                      renderDataSourceAssign()
                    )
                  ) : (
                    <Title theme={theme.photoDocumentDetails.title}>
                      {intl.formatMessage({
                        id: 'ANNOTATION.LOADING_DATA_SOURCE',
                      })}
                    </Title>
                  )
                ) : (
                  renderDataSourceAssign()
                )}
                <Button
                  theme={theme.button.default}
                  widthAuto
                  noMask
                  small
                  onClick={() => handleSaveAnnotation()}
                  disabled={isLoading}
                >
                  {isLoading
                    ? intl.formatMessage({ id: 'ANNOTATION.PLEASE_WAIT' })
                    : intl.formatMessage({ id: 'ANNOTATION.SAVE' })}
                </Button>
              </Header>
              <Content>
                {data.data_source_id ? (
                  isLoaded(dataSource) &&
                  isLoaded(dataSourcePhotoCategories) &&
                  isLoaded(dataSourceObjectsCategories) ? (
                    dataSource ? (
                      <>
                        {annotationType === 'PHOTO'
                          ? dataSourcePhotoCategories &&
                            dataSourcePhotoCategories.length > 0 &&
                            dataSourcePhotoCategories
                              .filter(
                                (category) =>
                                  category.id &&
                                  category.tags &&
                                  Object.values(category.tags).length > 0
                              )
                              .map((category, key) => (
                                <Fragment key={key}>
                                  {renderCategoryBox(annotationType, category)}
                                </Fragment>
                              ))
                          : ''}
                        {annotationType === 'OBJECT'
                          ? selectedObject !== undefined &&
                            dataSourceObjectsCategories &&
                            dataSourceObjectsCategories.length > 0 &&
                            dataSourceObjectsCategories
                              .filter(
                                (category) =>
                                  category.id &&
                                  category.tags &&
                                  Object.values(category.tags).length > 0
                              )
                              .map((category, key) => (
                                <Fragment key={key}>
                                  {renderCategoryBox(annotationType, category)}
                                </Fragment>
                              ))
                          : ''}
                      </>
                    ) : (
                      <NoResults
                        fullmode
                        label={intl.formatMessage({
                          id: 'ANNOTATION.NO_DATA_SOURCE',
                        })}
                      />
                    )
                  ) : (
                    <LoadingData fullmode />
                  )
                ) : (
                  <NoResults
                    fullmode
                    label={intl.formatMessage({
                      id: 'ANNOTATION.NO_DATA_SOURCE',
                    })}
                  />
                )}
              </Content>
            </ContentHolder>
          </Panel>
          <Button
            theme={theme.button.default}
            onClick={() => handleFinishAnnotation()}
            disabled={isLoading}
          >
            {isLoading
              ? intl.formatMessage({ id: 'ANNOTATION.PLEASE_WAIT' })
              : finishButtonMessage
              ? finishButtonMessage
              : intl.formatMessage({ id: 'ANNOTATION.FINISH_ANNOTATING' })}
          </Button>
        </Holder>
      </Wrapper>
      {visibleDataSourcesAddModal && (
        <DataSourceManage
          data={selectedDataSource}
          handleClose={() => {
            handleAction();
          }}
        />
      )}
    </>
  );
};

export const SingleDataAnnotation = ({
  data = {},
  handleDeletePhotoDocument = () => {},
  handleSaveAnnotation = () => {},
  handleFinishAnnotation = () => {},
  handleFilterAnnotation = () => {},
  handleChangeAnnotation = () => {},
  changedPhotoTagsAnnotation,
  changedObjectsAnnotation,
  handleSelectPhotoDocuments = () => {},
  selectedObject,
  handleSelectObject = () => {},
  previousAnnotation,
  handleChangePhotoDocument = () => {},
  isLoading = false,
}) => {
  const {
    id,
    origin_id,
    creation_date,
    received_date,
    automatic_annotation,
    vinne_annotation,
  } = data;
  const intl = useIntl();
  const [currentPhotoId, setCurrentPhotoId] = useState(undefined);
  const [
    photoTagsAutomaticAnnotation,
    setPhotoTagsAutomaticAnnotation,
  ] = useState(undefined);
  const [photoTagsVinneAnnotation, setPhotoTagsVinneAnnotation] = useState(
    undefined
  );
  const [objectsAutomaticAnnotation, setObjectsAutomaticAnnotation] = useState(
    undefined
  );
  const [objectsVinneAnnotation, setObjectsVinneAnnotation] = useState(
    undefined
  );

  const dataDetails = [
    {
      name: intl.formatMessage({ id: 'ANNOTATION.ORIGIN_ID' }),
      value: origin_id || intl.formatMessage({ id: 'ANNOTATION.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'ANNOTATION.DATE_CREATED' }),
      value: creation_date
        ? moment.unix(creation_date).format('YYYY-MM-DD')
        : intl.formatMessage({ id: 'ANNOTATION.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'ANNOTATION.DATE_RECEIVED' }),
      value: received_date
        ? moment.unix(received_date).format('YYYY-MM-DD')
        : intl.formatMessage({ id: 'ANNOTATION.NO_DATA' }),
    },
  ];

  useEffect(() => {
    setCurrentPhotoId(id);
  }, [id]);

  useEffect(
    () => {
      setPhotoTagsAutomaticAnnotation(
        automatic_annotation && automatic_annotation.photo
          ? (automatic_annotation.photo.tags &&
              Object.fromEntries(
                automatic_annotation.photo.tags.map((tag) => tag.split('.'))
              )) ||
              []
          : undefined
      );
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      automatic_annotation &&
        automatic_annotation.photo &&
        automatic_annotation.photo.tags,
    ]
  );

  useEffect(
    () => {
      setPhotoTagsVinneAnnotation(
        vinne_annotation && vinne_annotation.photo
          ? (vinne_annotation.photo.tags &&
              Object.fromEntries(
                vinne_annotation.photo.tags.map((tag) => tag.split('.'))
              )) ||
              []
          : undefined
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vinne_annotation && vinne_annotation.photo && vinne_annotation.photo.tags]
  );

  useEffect(
    () => {
      setObjectsAutomaticAnnotation(
        automatic_annotation && automatic_annotation.objects
          ? (automatic_annotation.objects.data &&
              automatic_annotation.objects.data.map((object) => ({
                ...object,
                tags:
                  (object.tags &&
                    Object.fromEntries(
                      object.tags.map((tag) => tag.split('.'))
                    )) ||
                  [],
              }))) ||
              []
          : undefined
      );
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      automatic_annotation &&
        automatic_annotation.objects &&
        automatic_annotation.objects.data,
    ]
  );

  useEffect(
    () => {
      setObjectsVinneAnnotation(
        vinne_annotation && vinne_annotation.objects
          ? (vinne_annotation.objects.data &&
              vinne_annotation.objects.data.map((object) => ({
                ...object,
                tags:
                  (object.tags &&
                    Object.fromEntries(
                      object.tags.map((tag) => tag.split('.'))
                    )) ||
                  [],
              }))) ||
              []
          : undefined
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      vinne_annotation &&
        vinne_annotation.objects &&
        vinne_annotation.objects.data,
    ]
  );

  return (
    <SingleAnnotation
      data={data}
      dataDetails={dataDetails}
      handleDeletePhotoDocument={handleDeletePhotoDocument}
      handleSaveAnnotation={handleSaveAnnotation}
      handleFinishAnnotation={handleFinishAnnotation}
      handleFilterAnnotation={handleFilterAnnotation}
      handleChangeAnnotation={handleChangeAnnotation}
      photoTagsAnnotation={
        photoTagsVinneAnnotation || photoTagsAutomaticAnnotation
      }
      changedPhotoTagsAnnotation={changedPhotoTagsAnnotation}
      objectsAnnotation={objectsVinneAnnotation || objectsAutomaticAnnotation}
      changedObjectsAnnotation={changedObjectsAnnotation}
      handleSelectPhotoDocuments={handleSelectPhotoDocuments}
      selectedObject={selectedObject}
      handleSelectObject={handleSelectObject}
      isLoading={isLoading || currentPhotoId !== id}
      availableRejectAnnotation={true}
      availablePreviousAnnotation={true}
      previousAnnotation={previousAnnotation}
      handleChangePhotoDocument={handleChangePhotoDocument}
      availableDeleteAnnotation={true}
    />
  );
};

export const SingleAnnotationVerification = ({
  data = {},
  handleSaveAnnotation = () => {},
  handleFinishAnnotation = () => {},
  handleFilterAnnotation = () => {},
  handleChangeAnnotation = () => {},
  changedPhotoTagsAnnotation,
  changedObjectsAnnotation,
  handleSelectPhotoDocuments = () => {},
  selectedObject,
  handleSelectObject = () => {},
  isLoading = false,
  isSequence = false,
  sequence = [],
}) => {
  const { id, vinne_annotation } = data;
  const intl = useIntl();
  const [currentPhotoId, setCurrentPhotoId] = useState(undefined);
  const [photoTagsVinneAnnotation, setPhotoTagsVinneAnnotation] = useState(
    undefined
  );
  const [objectsVinneAnnotation, setObjectsVinneAnnotation] = useState(
    undefined
  );

  const user = useSelector(
    (state) =>
      vinne_annotation &&
      vinne_annotation.user_id &&
      state.firestore.data[`users/${vinne_annotation.user_id}`]
  );

  const dataDetails = [
    {
      name: intl.formatMessage({ id: 'ANNOTATION.TAGGED_BY' }),
      value:
        vinne_annotation && vinne_annotation.user_id
          ? (user && user.full_name) || vinne_annotation.user_id
          : intl.formatMessage({ id: 'ANNOTATION.NO_DATA' }),
    },
    {
      name: intl.formatMessage({ id: 'ANNOTATION.DATE' }),
      value:
        vinne_annotation && vinne_annotation.end_date
          ? moment.unix(vinne_annotation.end_date).format('YYYY-MM-DD HH:mm:ss')
          : intl.formatMessage({ id: 'ANNOTATION.NO_DATA' }),
    },
  ];

  const firestoreConnect = [];
  if (vinne_annotation && vinne_annotation.user_id) {
    firestoreConnect.push({
      collection: 'users',
      doc: vinne_annotation.user_id,
      storeAs: `users/${vinne_annotation.user_id}`,
    });
  }
  useFirestoreConnect(firestoreConnect);

  useEffect(() => {
    setCurrentPhotoId(id);
  }, [id]);

  useEffect(
    () => {
      setPhotoTagsVinneAnnotation(
        vinne_annotation && vinne_annotation.photo
          ? (vinne_annotation.photo.tags &&
              Object.fromEntries(
                vinne_annotation.photo.tags.map((tag) => tag.split('.'))
              )) ||
              []
          : undefined
      );
      return () => {
        setPhotoTagsVinneAnnotation(undefined);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vinne_annotation && vinne_annotation.photo && vinne_annotation.photo.tags]
  );

  useEffect(
    () => {
      setObjectsVinneAnnotation(
        vinne_annotation && vinne_annotation.objects
          ? (vinne_annotation.objects.data &&
              vinne_annotation.objects.data.map((object) => ({
                ...object,
                tags:
                  (object.tags &&
                    Object.fromEntries(
                      object.tags.map((tag) => tag.split('.'))
                    )) ||
                  [],
              }))) ||
              []
          : undefined
      );
      return () => {
        setObjectsVinneAnnotation(undefined);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // eslint-disable-next-line react-hooks/exhaustive-deps
      vinne_annotation &&
        vinne_annotation.objects &&
        vinne_annotation.objects.data,
    ]
  );

  return (
    <SingleAnnotation
      data={data}
      dataDetails={dataDetails}
      handleSaveAnnotation={handleSaveAnnotation}
      handleFinishAnnotation={handleFinishAnnotation}
      handleFilterAnnotation={handleFilterAnnotation}
      handleChangeAnnotation={handleChangeAnnotation}
      photoTagsAnnotation={photoTagsVinneAnnotation}
      changedPhotoTagsAnnotation={changedPhotoTagsAnnotation}
      objectsAnnotation={objectsVinneAnnotation}
      changedObjectsAnnotation={changedObjectsAnnotation}
      handleSelectPhotoDocuments={handleSelectPhotoDocuments}
      selectedObject={selectedObject}
      handleSelectObject={handleSelectObject}
      isLoading={isLoading || currentPhotoId !== id}
      finishButtonMessage={intl.formatMessage({
        id: 'ANNOTATION.FINISH_VERIFICATION',
      })}
      isSequence={isSequence}
      sequence={sequence}
    />
  );
};

SingleAnnotation.propTypes = {
  data: PropTypes.object,
  dataDetails: PropTypes.array,
  handleDeletePhotoDocument: PropTypes.func,
  handleSaveAnnotation: PropTypes.func,
  handleFinishAnnotation: PropTypes.func,
  handleChangeAnnotation: PropTypes.func,
  photoTagsAnnotation: PropTypes.object,
  changedPhotoTagsAnnotation: PropTypes.object,
  objectsAnnotation: PropTypes.array,
  changedObjectsAnnotation: PropTypes.array,
  handleSelectPhotoDocuments: PropTypes.func,
  selectedObject: PropTypes.number,
  handleSelectObject: PropTypes.func,
  isLoading: PropTypes.bool,
  availableRejectAnnotation: PropTypes.bool,
  availablePreviousAnnotation: PropTypes.bool,
  previousAnnotation: PropTypes.array,
  handleChangePhotoDocument: PropTypes.func,
  availableDeleteAnnotation: PropTypes.bool,
  finishButtonMessage: PropTypes.string,
  isSequence: PropTypes.bool,
  sequence: PropTypes.array,
};

SingleDataAnnotation.propTypes = {
  data: PropTypes.object,
  handleDeletePhotoDocument: PropTypes.func,
  handleSaveAnnotation: PropTypes.func,
  handleFinishAnnotation: PropTypes.func,
  handleChangeAnnotation: PropTypes.func,
  changedPhotoTagsAnnotation: PropTypes.object,
  changedObjectsAnnotation: PropTypes.array,
  handleSelectPhotoDocuments: PropTypes.func,
  selectedObject: PropTypes.number,
  handleSelectObject: PropTypes.func,
  previousAnnotation: PropTypes.array,
  handleChangePhotoDocument: PropTypes.func,
  isLoading: PropTypes.bool,
};

SingleAnnotationVerification.propTypes = {
  data: PropTypes.object,
  handleSaveAnnotation: PropTypes.func,
  handleFinishAnnotation: PropTypes.func,
  handleChangeAnnotation: PropTypes.func,
  changedPhotoTagsAnnotation: PropTypes.object,
  changedObjectsAnnotation: PropTypes.array,
  handleSelectPhotoDocuments: PropTypes.func,
  selectedObject: PropTypes.number,
  handleSelectObject: PropTypes.func,
  isLoading: PropTypes.bool,
  isSequence: PropTypes.bool,
  sequence: PropTypes.array,
};
